import React, { useState, useEffect } from 'react';
import { ChartBarIcon, CurrencyDollarIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../utils/api';

const Financial = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/api/financial-documents'));
      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }
      const data = await response.json();
      setDocuments(data);
    } catch (err) {
      setError(err.message);
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ka-GE');
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const response = await fetch(getApiUrl(`/api/financial-documents/${documentId}/download`));
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || `financial-document-${documentId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('ფაილის ჩამოტვირთვის შეცდომა');
    }
  };

  const financialData = {
    revenue: {
      total: 2500000,
      breakdown: [
        { source: "სპონსორობა", amount: 1200000, percentage: 48 },
        { source: "ბილეთები", amount: 800000, percentage: 32 },
        { source: "მერჩენდაიზი", amount: 300000, percentage: 12 },
        { source: "სხვა", amount: 200000, percentage: 8 }
      ]
    },
    expenses: {
      total: 2200000,
      breakdown: [
        { category: "ფეხბურთელების ხელფასები", amount: 1000000, percentage: 45 },
        { category: "სტადიონის მართვა", amount: 400000, percentage: 18 },
        { category: "ტრენინგის ბაზა", amount: 300000, percentage: 14 },
        { category: "ადმინისტრაცია", amount: 250000, percentage: 11 },
        { category: "სხვა", amount: 250000, percentage: 12 }
      ]
    }
  };


  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ka-GE', {
      style: 'currency',
      currency: 'GEL',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        
        {/* Financial Overview */}
        



        {/* Revenue Breakdown */}




        {/* Financial Reports */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-8">
            ფინანსური ანგარიშგებები
          </h2>
          
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 font-georgian mb-4">შეცდომა: {error}</p>
              <button 
                onClick={loadDocuments}
                className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded font-georgian"
              >
                ხელახლა ცდა
              </button>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-georgian">ფინანსური ანგარიშგებები ჯერ არ არის ხელმისაწვდომი</p>
            </div>
          ) : (
            <div className="space-y-6">
              {documents.map((document) => (
                <div key={document.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <DocumentTextIcon className="h-6 w-6 text-red-500 mr-3" />
                        <h3 className="text-lg font-semibold text-gray-900 font-georgian">
                          {document.title}
                        </h3>
                      </div>
                      {document.description && (
                        <p className="text-gray-600 font-georgian text-sm mb-2">
                          {document.description}
                        </p>
                      )}
                      <div className="flex items-center space-x-4 text-sm text-gray-500 font-georgian">
                        {document.file_size && (
                          <span>{formatFileSize(document.file_size)}</span>
                        )}
                        <span>{formatDate(document.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <button 
                    onClick={() => handleDownload(document.id, document.file_name)}
                    className="text-primary-600 hover:text-primary-700 font-medium text-sm font-georgian flex items-center"
                  >
                    <DocumentTextIcon className="h-4 w-4 mr-2" />
                    ანგარიშგების ჩამოტვირთვა →
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Financial;
